import type {
  CommonResponseChaptersResponse,
  CommonResponseCollectionCurriculumResponse,
  CommonResponseCoreCurriculumDto,
  CurriculumDifferenceRequest,
  GetActiveCurriculumsByIdsAndTypeParams,
  GetChaptersParams,
  GetCurriculumDifferenceByPublisherAndTrieKeyData,
  GetCurriculumsByKeyAndTypeParams,
  GetCurriculumsByKeyData,
  GetCurriculumsByKeyParams,
  GetCurriculumsBySchoolData,
  GetCurriculumsBySchoolParams,
  GetCurriculumsBySchoolTestIdData,
  GetCurriculumsBySchoolTestIdParams,
  GetCurriculumTreeNodesByDepthData,
  GetPublisherAndCurriculumData,
  GetPublisherAndCurriculumParams,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'

import {
  adaptCurriculumChapterSearch,
  adaptCurriculumChapterTrees,
  adaptCurriculumConceptChapterTrees,
  adaptCurriculumConceptTreeNodes,
  adaptCurriculumCoreNodes,
  adaptCurriculums,
  adaptPublisherCurriculumDifference,
  adaptPublisherCurriculums,
  toCurriculumCore,
} from './adapters'
import type {
  Curriculum,
  CurriculumChapterSearch,
  CurriculumChapterTree,
  CurriculumConceptChapterTree,
  CurriculumConceptTreeNode,
  CurriculumCore,
  PublisherCurriculum,
  PublisherCurriculumDifference,
} from './schemas'

export type CurriculumsByIdsParams = GetActiveCurriculumsByIdsAndTypeParams
export type NestedCurriculumsParams = GetCurriculumsByKeyAndTypeParams
export type CurriculumChapterTreesByKeyParams = GetCurriculumsByKeyParams
// 서버는 학교별 기출 두 조회의 선택 조건을 nullable(`ContentRevision?`·`Int?`·`Set<Int>?` 등)로 받지만 스펙이
// optional 로만 그린다. 고등은 학기가 없어 null 을 싣는다. 2026-10-07 백엔드 develop 의
// curriculum/controller/CurriculumController.kt(getCurriculumsBySchool·getCurriculumsBySchoolTestId)로 확인.
export type CurriculumChapterTreesBySchoolParams =
  WithNullableOptionals<GetCurriculumsBySchoolParams>
export type CurriculumChapterTreesBySchoolTestParams =
  WithNullableOptionals<GetCurriculumsBySchoolTestIdParams>
export type CurriculumChapterSearchParams = GetChaptersParams

export const getCurriculumsByIds = async (
  params: CurriculumsByIdsParams,
  meta?: RequestMeta,
): Promise<Curriculum[]> => {
  const body = await httpClient().get<CommonResponseCollectionCurriculumResponse>('/curriculums', {
    params,
    meta,
  })
  return adaptCurriculums(body.data) ?? []
}

export const getNestedCurriculums = async (
  params: NestedCurriculumsParams,
  meta?: RequestMeta,
): Promise<Curriculum[]> => {
  const body = await httpClient().get<CommonResponseCollectionCurriculumResponse>(
    '/curriculums/nested',
    { params, meta },
  )
  return adaptCurriculums(body.data) ?? []
}

export const getCurriculumCore = async (meta?: RequestMeta): Promise<CurriculumCore> => {
  const body = await httpClient().get<CommonResponseCoreCurriculumDto>('/curriculums/core', {
    meta,
  })
  return toCurriculumCore(adaptCurriculumCoreNodes(body.data))
}

// 아래 셋은 서버가 CommonResponse 봉투 없이 목록을 그대로 보낸다.
export const getCurriculumChapterTreesByKey = async (
  params: CurriculumChapterTreesByKeyParams,
  meta?: RequestMeta,
): Promise<CurriculumConceptChapterTree[]> => {
  const body = await httpClient().get<GetCurriculumsByKeyData>('/curriculums/by-key', {
    params,
    meta,
  })
  return adaptCurriculumConceptChapterTrees(body) ?? []
}

export const getCurriculumChapterTreesBySchool = async (
  params: CurriculumChapterTreesBySchoolParams,
  meta?: RequestMeta,
): Promise<CurriculumChapterTree[]> => {
  const body = await httpClient().get<GetCurriculumsBySchoolData>('/curriculums/by-school', {
    params,
    meta,
  })
  return adaptCurriculumChapterTrees(body) ?? []
}

export const getCurriculumChapterTreesBySchoolTest = async (
  params: CurriculumChapterTreesBySchoolTestParams,
  meta?: RequestMeta,
): Promise<CurriculumChapterTree[]> => {
  const body = await httpClient().get<GetCurriculumsBySchoolTestIdData>(
    '/curriculums/by-school-test',
    { params, meta },
  )
  return adaptCurriculumChapterTrees(body) ?? []
}

export const getCurriculumChapterSearch = async (
  params: CurriculumChapterSearchParams,
  meta?: RequestMeta,
): Promise<CurriculumChapterSearch> => {
  const body = await httpClient().get<CommonResponseChaptersResponse>(
    '/curriculums/middle-chapters',
    { params, meta },
  )
  return adaptCurriculumChapterSearch(body.data)
}

/**
 * 교육과정 트리를 depth(0~4) 깊이까지 펼친 노드 목록. 초·중은 학기까지, 고등은 학교급 바로 아래 과목에서 멈춘다.
 * 2026-10-14 백엔드 develop(6a0f8e7bb) 의 curriculum/controller/CurriculumController.kt(getCurriculumTreeNodesByDepth)·
 * model/dto/CurriculumTreeNodeDto.kt(flattenByDepth)로 확인.
 */
export const getCurriculumConceptTreeNodes = async (
  depth: number,
  meta?: RequestMeta,
): Promise<CurriculumConceptTreeNode[]> => {
  const body = await httpClient().get<GetCurriculumTreeNodesByDepthData>(
    `/curriculums/tree/group/${depth}`,
    { meta },
  )
  return adaptCurriculumConceptTreeNodes(body.data) ?? []
}

/***************** 출판사 교과서 단원 순서 *****************/
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 curriculum/controller/ProblemCurriculumDifferenceController.kt 로 확인.

export type PublisherCurriculumParams = GetPublisherAndCurriculumParams
export type PublisherCurriculumDifferenceBody = CurriculumDifferenceRequest

/** 개정(trieKey)의 교과서를 낸 출판사와 그 교과서가 있는 학기·과목 */
export const getPublisherCurriculums = async (
  params: PublisherCurriculumParams,
  meta?: RequestMeta,
): Promise<PublisherCurriculum[]> => {
  const body = await httpClient().get<GetPublisherAndCurriculumData>('/publisher/curriculum', {
    params,
    meta,
  })
  return adaptPublisherCurriculums(body.data) ?? []
}

/** 조회지만 서버가 POST 로 받는다 */
export const getPublisherCurriculumDifference = async (
  body: PublisherCurriculumDifferenceBody,
  meta?: RequestMeta,
): Promise<PublisherCurriculumDifference> => {
  const response = await httpClient().post<GetCurriculumDifferenceByPublisherAndTrieKeyData>(
    '/publisher/curriculum-difference',
    body,
    { meta },
  )
  return adaptPublisherCurriculumDifference(response.data)
}
